import { defineMessages, useIntl } from 'react-intl';
import { Wishlist32 } from '@packages/themes/icons';
import { Button } from '@packages/shared';
import { LoginDialogComposed } from '@packages/modules/src/LoginDialog/LoginDialogComposed/LoginDialogComposed';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useEffect } from 'react';
import type { GTMEventGlycerinDisplayWishlist } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';

const messages = defineMessages({
  emptyWishlistHeadlineLoggedIn: {
    id: 'WishlistEmpty.LoggedIn.Headline',
    defaultMessage: 'Mein Merkzettel',
  },
  emptyWishlistHeadlineLoggedOut: {
    id: 'WishlistEmpty.LoggedOut.Headline',
    defaultMessage: 'Entdecken Sie Ihre Lieblingsartikel',
  },
  emptyWishlistTextLoggedIn: {
    id: 'WishlistEmpty.LoggedIn.InfoText',
    defaultMessage: 'Sie haben keine Artikel auf Ihrem Merkzettel.',
  },
  emptyWishlistTextLoggedOut: {
    id: 'WishlistEmpty.LoggedOut.InfoText',
    defaultMessage: 'Suchen Sie nach Artikeln, die Sie sich bereits gemerkt haben?',
  },
  emptyWishlistAdditionalTextLoggedIn: {
    id: 'WishlistEmpty.LoggedIn.InfoText2',
    defaultMessage: 'Speichern Sie Ihre Lieblingsartikel, um sie nicht aus den Augen zu verlieren.',
  },
  emptyWishlistAdditionalTextLoggedOut: {
    id: 'WishlistEmpty.LoggedOut.InfoText2',
    defaultMessage:
      'Melden Sie sich an, um Ihren Merkzettel zu sehen oder um weitere Artikel auf all Ihren Geräten zu speichern.',
  },
  backToShopButton: {
    id: 'WishlistEmpty.Action.GoToShop',
    defaultMessage: 'Weiter im Shop',
  },
  loginButton: {
    id: 'WishlistEmpty.Action.GoToLogin',
    defaultMessage: 'Jetzt anmelden',
  },
});

export const WishlistEmpty: React.FC = () => {
  const { formatMessage } = useIntl();
  const { loggedIn } = useLoginState();
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    dispatchGTMEvent<GTMEventGlycerinDisplayWishlist>({
      event: 'DisplayWishList',
      DisplayWishListData: {
        products: [],
      },
      _clear: true,
    });
  }, [dispatchGTMEvent]);

  return (
    <div className="flex flex-col items-center justify-between text-center">
      <Wishlist32
        sx={{
          width: '6.25rem',
          height: '6.25rem',
          mt: { xs: '2.5rem', md: '5rem' },
          mb: '0.688rem',
        }}
      />
      <h3 className="text-size-h3 text-color-text mb-1.5 text-center">
        {formatMessage(
          loggedIn
            ? messages.emptyWishlistHeadlineLoggedIn
            : messages.emptyWishlistHeadlineLoggedOut,
        )}
      </h3>
      <div className="text-size-body1 text-color-text-secondary text-center">
        {formatMessage(
          loggedIn ? messages.emptyWishlistTextLoggedIn : messages.emptyWishlistTextLoggedOut,
        )}
      </div>
      <div className="text-size-body1 text-color-text-secondary mb-4 text-center">
        {formatMessage(
          loggedIn
            ? messages.emptyWishlistAdditionalTextLoggedIn
            : messages.emptyWishlistAdditionalTextLoggedOut,
        )}
      </div>
      <div className="flex flex-col justify-between gap-2.5">
        {!loggedIn && (
          <LoginDialogComposed>
            {(openDialog) => (
              <Button
                variant="contained"
                color="primary"
                onClick={openDialog}
                sx={{ width: '16.313rem', maxWidth: '100%' }}
              >
                {formatMessage(messages.loginButton)}
              </Button>
            )}
          </LoginDialogComposed>
        )}
        <Button
          variant="contained"
          color={loggedIn ? 'primary' : undefined}
          sx={{ width: '16.313rem', maxWidth: '100%' }}
          href="/"
        >
          {formatMessage(messages.backToShopButton)}
        </Button>
      </div>
    </div>
  );
};
